import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { config } from '../config';
import { setLanguage } from '../i18n';
import { ApiError, api, setConsentRequiredHandler, setToken, setUnauthorizedHandler, type Me } from './api';
import { rememberLocale } from './early-start';
import { getInitData, isInTelegram } from './telegram';

type AuthState =
  | { status: 'loading' }
  | { status: 'ready'; me: Me; startParam: string | null }
  | { status: 'outside_telegram' }
  | { status: 'error'; error: ApiError };

interface AuthContextValue {
  state: AuthState;
  me: Me | null;
  setMe: (me: Me) => void;
  retry: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface LoginResponse {
  token: string;
  expiresAt: string;
  startParam: string | null;
  me: Me;
}

type LoginOutcome = { kind: 'ok'; res: LoginResponse } | { kind: 'outside' };

async function doLogin(): Promise<LoginOutcome> {
  if (isInTelegram()) return { kind: 'ok', res: await api<LoginResponse>('/auth/telegram', { method: 'POST', json: { initData: getInitData() } }) };
  if (config.devAuth) {
    return {
      kind: 'ok',
      res: await api<LoginResponse>('/auth/dev-login', { method: 'POST', json: { telegramId: config.devTelegramId, firstName: 'Dev', username: null } }),
    };
  }
  return { kind: 'outside' };
}

let earlyLogin: Promise<LoginOutcome> | null = null;
/** Запустить вход до первой отрисовки (вызывается из main.tsx); первая попытка AuthProvider использует этот запрос. */
export function startLoginEarly() {
  earlyLogin ??= doLogin();
  earlyLogin.catch(() => undefined); // ошибка обработается в AuthProvider
}

/**
 * Вход: initData из Telegram отправляется на сервер, который проверяет подпись и выдаёт токен.
 * Токен хранится только в памяти вкладки — при каждом запуске Mini App вход выполняется заново
 * по свежей initData (это дёшево и не требует хранить секреты на устройстве).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function login() {
      setState({ status: 'loading' });
      try {
        // Первая попытка — уже запущенный ранний запрос (он же при повторном запуске эффекта, без второго входа);
        // повторные попытки (retry, истёкшая сессия) — новый запрос.
        const pending = attempt === 0 ? (earlyLogin ??= doLogin()) : doLogin();
        const outcome = await pending;
        if (outcome.kind === 'outside') {
          if (!cancelled) setState({ status: 'outside_telegram' });
          return;
        }
        const res = outcome.res;
        setToken(res.token);
        rememberLocale(res.me.settings.locale);
        await setLanguage(res.me.settings.locale);
        if (!cancelled) setState({ status: 'ready', me: res.me, startParam: res.startParam });
      } catch (e) {
        if (!cancelled) setState({ status: 'error', error: e instanceof ApiError ? e : new ApiError(0, 'NETWORK_ERROR', String(e)) });
      }
    }
    void login();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    // Истёкшая сессия → повторный вход по initData.
    setUnauthorizedHandler(() => setAttempt((a) => a + 1));
    // Версия правил могла измениться во время сессии — перечитываем профиль (там актуальная версия).
    setConsentRequiredHandler(() => {
      void api<Me>('/users/me')
        .then((me) => setState((s) => (s.status === 'ready' ? { ...s, me } : s)))
        .catch(() => undefined);
    });
  }, []);

  const setMe = useCallback((me: Me) => {
    setState((s) => (s.status === 'ready' ? { ...s, me } : s));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, me: state.status === 'ready' ? state.me : null, setMe, retry: () => setAttempt((a) => a + 1) }),
    [state, setMe],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** Текущий пользователь (только внутри защищённых экранов). */
export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error('useMe used before login');
  return me;
}
